import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { ApiError } from "../api/client";
import { submitReview, type BookingDetail } from "../api/bookings";
import { Button, Card } from "./ui";
import { StarDisplay, StarRating } from "./rating";

/**
 * Kartu penilaian bintang 1–5 untuk sesi yang sudah selesai.
 * Muncul setelah terapis menandai sesi COMPLETED, dan hanya bisa dikirim sekali.
 */
export function BookingReviewCard({ booking }: { booking: BookingDetail }) {
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (booking.review) {
    return (
      <Card className="review-card">
        <strong>Penilaian Anda</strong>
        <StarDisplay
          rating={booking.review.rating}
          label={`${booking.review.rating} dari 5 bintang`}
        />
        {booking.review.comment ? <p style={{ margin: 0 }}>{booking.review.comment}</p> : null}
        <p className="state-hint" style={{ margin: 0 }}>
          Terima kasih — penilaian Anda membantu menjaga kualitas layanan.
        </p>
      </Card>
    );
  }

  if (booking.status !== "COMPLETED") return null;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await submitReview(booking.id, { rating, comment: comment || undefined });
      await queryClient.invalidateQueries({ queryKey: ["booking", booking.id] });
      await queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Gagal mengirim penilaian. Coba lagi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="review-card">
      <strong>
        <Sparkles size={14} /> Nilai layanan terapis
      </strong>
      <p className="state-hint" style={{ margin: 0 }}>
        Sesi bersama {booking.therapist.name} sudah selesai. Berapa bintang untuk kualitas
        layanannya?
      </p>
      <StarRating value={rating} onChange={setRating} disabled={busy} />
      <textarea
        className="input"
        rows={2}
        value={comment}
        placeholder="Komentar (opsional) — mis. keluhan yang tertangani, keramahan…"
        onChange={(e) => setComment(e.target.value)}
      />
      {error ? <p className="alert alert-error">{error}</p> : null}
      <Button onClick={() => void send()} loading={busy}>
        Kirim Penilaian
      </Button>
    </Card>
  );
}
